'use client';

import { useState, useRef, useEffect } from 'react';
import debounce from 'lodash-es/debounce';
import { useNumberInput } from '@shared/ui';

interface UseProductCountProps {
    productQuantity: number;
    onChange: (value: number) => void;
    min: number;
    max?: number;
}

export function useProductCount(props: UseProductCountProps) {
    const { productQuantity = 1, onChange, min, max } = props;

    const [inputValue, setInputValue] = useState(productQuantity);

    const debouncedOnChange = useRef(
        debounce((value: number) => onChange(value), 1000)
    ).current;

    const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
        useNumberInput({
            step: 1,
            value: inputValue,
            min,
            max,
            precision: 0,
            onChange(_, valueAsNumber) {
                const newVal = Math.max(valueAsNumber, 1);

                setInputValue(Number.isNaN(newVal) ? min : newVal);
            },
        });

    const inc = getIncrementButtonProps();

    const dec = getDecrementButtonProps();

    const input = getInputProps();

    useEffect(() => {
        debouncedOnChange(inputValue);
    }, [inputValue]);

    useEffect(() => {
        if (max && inputValue > max) {
            setInputValue(max);
        }
    }, [max, inputValue]);

    return {
        inc,
        dec,
        input,
    };
}
